import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahg21roh653ivfvntug";

type FormStatus = "idle" | "submitting" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

const reportTypes = [
  "重磅爆料",
  "惊天大瓜",
  "猛料曝光",
  "年度大瓜",
  "超级大瓜",
  "内容纠错",
  "其他事项",
];

export default function ContactForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot !== "") {
      setStatus("success");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setDetailLength(0);
        form.reset();
        return;
      }

      setFormError(
        typeof serverMsg === "string" && serverMsg.trim() !== ""
          ? serverMsg
          : "提交失败，请稍后重试或直接发送邮件给我们。",
      );
      setStatus("error");
    } catch {
      setFormError("网络异常，提交失败，请稍后重试或直接发送邮件给我们。");
      setStatus("error");
    }
  };

  return (
    <form
      id="guagua-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <h2 className="mb-1.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
        瓜友投稿 / 联系编辑部
      </h2>
      <p className="mb-6 text-sm leading-relaxed text-foreground-600">
        带星号的为必填项。附上原帖链接与时间，能大幅提高材料被采用的概率。
      </p>

      {status === "success" && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800">
          <i className="ri-checkbox-circle-fill mt-0.5 text-primary-600" />
          <span>投稿已收到，感谢你的线索。编辑部会在核对后决定是否整理成时间线，如有需要会通过邮箱联系你。</span>
        </div>
      )}

      {status === "error" && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-100 p-4 text-sm text-primary-900">
          <i className="ri-error-warning-fill mt-0.5 text-primary-700" />
          <span>{formError}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-foreground-800">
            你的称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            placeholder="例如：吃瓜第一排"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            联系邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="方便我们核实时联系你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        <div>
          <label htmlFor="report_type" className="mb-1.5 block text-sm font-medium text-foreground-800">
            提交类型 <span className="text-primary-600">*</span>
          </label>
          <select
            id="report_type"
            name="report_type"
            required
            defaultValue=""
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          >
            <option value="" disabled>
              请选择
            </option>
            {reportTypes.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
            涉及账号或事件主体
          </label>
          <input
            id="subject"
            name="subject"
            type="text"
            placeholder="例如：某美食博主 / 某直播事件"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="source_url" className="mb-1.5 block text-sm font-medium text-foreground-800">
            原帖链接
          </label>
          <input
            id="source_url"
            name="source_url"
            type="url"
            placeholder="https:// 粘贴原始爆料帖或视频链接"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>

        <div className="sm:col-span-2">
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="detail" className="block text-sm font-medium text-foreground-800">
              详细说明 <span className="text-primary-600">*</span>
            </label>
            <span className="text-xs text-foreground-500">{detailLength}/500</span>
          </div>
          <textarea
            id="detail"
            name="detail"
            required
            maxLength={500}
            rows={5}
            onChange={(event) => setDetailLength(event.target.value.length)}
            placeholder="请简述事件经过、发生时间与关键信息，最多 500 字。"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
      </div>

      <div className="form-honeypot-field" aria-hidden="true">
        <label htmlFor="website_alt">请勿填写此栏</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你确认材料来自公开渠道，并同意我们在核实后整理发布。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} />
          {status === "submitting" ? "提交中…" : "提交投稿"}
        </button>
      </div>
    </form>
  );
}